import React, { useState, useEffect } from 'react';
import { Camera, Upload, X, ChevronLeft } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { getMerchantHeaderClasses, getMerchantButtonClasses } from '../utils/merchantColors';

/**
 * Dynamic Photo Upload Component
 *
 * Handles photo upload based on section configuration
 * Supports both booking-level and item-level photos
 */
const DynamicPhotoUpload = ({
  config,
  language = 'de',
  merchant = null,
  itemData = null, // For item-level photos: { bookingItem, inventoryItem }
  initialPhotos = [], // Array of File objects to initialize with
  onPhotosChange,
  onComplete,
  onBack = null // For item-level: callback to navigate to previous item
}) => {
  console.log('[DynamicPhotoUpload] Rendering - minPhotos:', config?.min_photos, 'itemData:', itemData?.bookingItem?.item_name, 'initialPhotos:', initialPhotos?.length);

  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const minPhotos = config?.min_photos || 0;
  const maxPhotos = config?.max_photos || 10;

  // Initialize photos from initialPhotos prop (when navigating back)
  useEffect(() => {
    if (initialPhotos && initialPhotos.length > 0) {
      console.log('[DynamicPhotoUpload] Initializing with', initialPhotos.length, 'photos from initialPhotos prop');

      // Convert File objects to internal format with preview URLs
      const photosWithPreviews = initialPhotos.map((file, index) => ({
        file,
        preview: URL.createObjectURL(file),
        timestamp: Date.now() + index // Add index to make timestamps unique
      }));

      setPhotos(photosWithPreviews);
    }

    // Cleanup function to revoke object URLs when component unmounts
    return () => {
      photos.forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, []); // Only run on mount

  console.log('[DynamicPhotoUpload] Computed minPhotos:', minPhotos, 'maxPhotos:', maxPhotos, 'currentPhotos:', photos.length);
  const headerText = config?.header_text?.[language] || 'Photos';
  const instructions = config?.instructions?.[language] || '';
  const showInstructions = config?.show_instructions !== false;

  // Replace placeholders in text if item-level
  const getDisplayText = (text) => {
    if (!text || !itemData) return text;

    let result = text;
    if (itemData.bookingItem) {
      result = result.replace(/{item_name}/g, itemData.bookingItem.item_name || '');
      result = result.replace(/{size}/g, itemData.bookingItem.size || '');
      result = result.replace(/{code}/g, itemData.bookingItem.code || '');
    }
    if (itemData.inventoryItem) {
      result = result.replace(/{item_number}/g, itemData.inventoryItem.item_number || '');
    }
    return result;
  };

  const handlePhotoCapture = async (event) => {
    console.log('[DynamicPhotoUpload] ▶ Photo capture triggered, files:', event.target.files?.length);

    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      console.log('[DynamicPhotoUpload] No files selected');
      return;
    }

    console.log('[DynamicPhotoUpload] Processing', files.length, 'files');
    setUploading(true);
    setError(null);

    try {
      const newPhotos = [];

      for (const file of files) {
        // Check if we've reached max photos
        if (photos.length + newPhotos.length >= maxPhotos) {
          setError(language === 'de'
            ? `Maximal ${maxPhotos} Fotos erlaubt`
            : `Maximum ${maxPhotos} photos allowed`
          );
          break;
        }

        // Compress image
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg'
        });

        // Create preview URL
        const previewUrl = URL.createObjectURL(compressedFile);

        newPhotos.push({
          file: compressedFile,
          preview: previewUrl,
          timestamp: Date.now()
        });
      }

      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      onPhotosChange?.(updatedPhotos.map(p => p.file));

    } catch (err) {
      console.error('Error processing photos:', err);
      setError(language === 'de'
        ? 'Fehler beim Verarbeiten der Fotos'
        : 'Error processing photos'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (index) => {
    const updatedPhotos = photos.filter((_, i) => i !== index);
    setPhotos(updatedPhotos);
    onPhotosChange?.(updatedPhotos.map(p => p.file));
  };

  const handleNext = () => {
    try {
      console.log('[DynamicPhotoUpload] ▶ handleNext clicked, photos:', photos.length, 'minPhotos:', minPhotos, 'canProceed:', photos.length >= minPhotos);

      if (photos.length < minPhotos) {
        console.log('[DynamicPhotoUpload] ❌ Not enough photos, showing error');
        setError(language === 'de'
          ? `Mindestens ${minPhotos} Foto${minPhotos > 1 ? 's' : ''} erforderlich`
          : `At least ${minPhotos} photo${minPhotos > 1 ? 's' : ''} required`
        );
        return;
      }

      console.log('[DynamicPhotoUpload] ✅ Calling onComplete with', photos.length, 'photos');
      onComplete?.(photos.map(p => p.file));
    } catch (error) {
      console.error('[DynamicPhotoUpload] ❌ ERROR in handleNext:', error);
      throw error;
    }
  };

  const handleSkip = () => {
    try {
      console.log('[DynamicPhotoUpload] ▶ handleSkip clicked, minPhotos:', minPhotos, 'canSkip:', minPhotos === 0);

      if (minPhotos > 0) {
        console.log('[DynamicPhotoUpload] ❌ Cannot skip, photos are required');
        setError(language === 'de'
          ? 'Fotos sind erforderlich'
          : 'Photos are required'
        );
        return;
      }

      console.log('[DynamicPhotoUpload] ✅ Calling onComplete with empty photos');
      onComplete?.([]);
    } catch (error) {
      console.error('[DynamicPhotoUpload] ❌ ERROR in handleSkip:', error);
      throw error;
    }
  };

  const canProceed = photos.length >= minPhotos;
  const canSkip = minPhotos === 0;

  console.log('[DynamicPhotoUpload] About to render, canProceed:', canProceed, 'canSkip:', canSkip, 'uploading:', uploading);

  try {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className={`${getMerchantHeaderClasses(merchant)} p-6`}>
            <h1 className="text-2xl font-bold">{getDisplayText(headerText)}</h1>
            {showInstructions && instructions && (
              <p className="mt-2 opacity-90">{getDisplayText(instructions)}</p>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {console.log('[DynamicPhotoUpload] ✓ Rendering content section')}

          {/* Photo Count */}
          <div className="text-center mb-6">
            <p className="text-gray-600">
              {photos.length} / {maxPhotos} {language === 'de' ? 'Fotos' : 'photos'}
              {minPhotos > 0 && (
                <span className="ml-2 text-sm">
                  ({language === 'de' ? 'mind.' : 'min.'} {minPhotos})
                </span>
              )}
            </p>
          </div>

          {console.log('[DynamicPhotoUpload] ✓ Rendering upload buttons')}

          {/* Upload Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:merchant-border-light hover:merchant-bg-light transition-colors">
              <Camera className="w-12 h-12 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">
                {language === 'de' ? 'Kamera' : 'Camera'}
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handlePhotoCapture}
              disabled={uploading || photos.length >= maxPhotos}
              className="hidden"
            />
          </label>

          <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:merchant-border-light hover:merchant-bg-light transition-colors">
            <Upload className="w-12 h-12 text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              {language === 'de' ? 'Galerie' : 'Gallery'}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoCapture}
              disabled={uploading || photos.length >= maxPhotos}
              className="hidden"
            />
          </label>
        </div>

        {/* Uploading Indicator */}
        {uploading && (
          <div className="text-center text-gray-600 mb-4">
            {language === 'de' ? 'Fotos werden verarbeitet...' : 'Processing photos...'}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Photo Grid */}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {photos.map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={photo.preview}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg shadow-md"
                />
                <button
                  onClick={() => handleRemovePhoto(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {photos.length === 0 && !uploading && (
          <div className="text-center text-gray-500 py-12">
            <Camera className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>{language === 'de' ? 'Keine Fotos hochgeladen' : 'No photos uploaded'}</p>
          </div>
        )}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex gap-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="flex items-center px-6 py-3 rounded-lg font-semibold transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {language === 'de' ? 'Zurück' : 'Back'}
                </button>
              )}
              {canSkip && (
                <button
                  onClick={handleSkip}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {language === 'de' ? 'Überspringen' : 'Skip'}
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!canProceed || uploading}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  canProceed && !uploading
                    ? getMerchantButtonClasses(merchant)
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {language === 'de' ? 'Weiter' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('[DynamicPhotoUpload] ❌ RENDER ERROR:', error);
    console.error('[DynamicPhotoUpload] Stack:', error.stack);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-red-700 font-bold mb-2">Render Error</h2>
          <p className="text-red-600">{error.message}</p>
          <pre className="text-xs mt-2 text-red-500 overflow-auto">{error.stack}</pre>
        </div>
      </div>
    );
  }
};

export default DynamicPhotoUpload;
